import React, {useEffect, useState} from "react";
import Card from "components/Cards/Card";
import ToggleButton from "components/ui/ToggleButton";
import keyQuestionsIcon from "assets/Icon_Tab_10.png";
import {v4 as uuidv4} from "uuid";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
  Legend
} from "recharts";
import {useCurrentPng} from "recharts-to-png";
import {ImageMap} from "../../Dashboard/Dashboard";
import reviewSummaryIcon from "../../../assets/Icon_Tab_08.png";
import LoadingIndicator from "../../ui/LoadingIndicator";
import { YOY_BAR_CHART_COLORS, YOY_LINE_CHART_COLORS } from "../../../constants/chartColors";

type KeyQsYoYProps = {
  href: string | null;
  data: ChartDataYoY;
  minWidth: string;
  defaultChartType: string;
  customName?: string;
  setImages?: React.Dispatch<React.SetStateAction<ImageMap>>;
  idx?: number;
  isLoading?: boolean;
};

type ChartDataYoY = {
  pieChartData: any;
  otherChartData: any[];
  options: {
    yAxisType: "Count" | "Percentage";
  };
  questions?: string[];
  periods?: string[];
};

// Use different colors based on chart type
const getChartColors = (chartType: string) => {
  return chartType === "Bar" ? YOY_BAR_CHART_COLORS : YOY_LINE_CHART_COLORS;
};

const checkNullChartData = (c: any[]) => {
  const data = c.find((v) => v);
  return !!data;
};

function percentageFormatter(item: any) {
  return parseFloat(item).toFixed(1) + "%";
}

const Tick: React.FC<any> = ({
  payload: { value },
  verticalAnchor,
  visibleTicksCount,
  ...rest
}) => (
  <Text
    {...rest}
    verticalAnchor={verticalAnchor}
    visibleTicksCount={visibleTicksCount}
    width={80}
    style={{ fontSize: '11px' }}
  >
    {value}
  </Text>
);

const ticks20 = [0, 20, 40, 60, 80, 100];

const KeyQuestionsYoY: React.FC<KeyQsYoYProps> = ({
  href,
  data,
  minWidth,
  defaultChartType,
  customName,
  setImages,
  idx,
  isLoading = true
}) => {
  const [keyQuestionsChartType, setKeyQuestionsChartType] = useState(
    defaultChartType ? defaultChartType : "Bar"
  );

  const handleSetKeyQuestionsChartType = (value: string) => {
    setKeyQuestionsChartType(value);
  };

  const [keyQuestionsPeriodToggles, setKeyQuestionsPeriodToggles] = useState<
    Record<string, boolean>
  >({});

  // Track internal loading state
  const [internalLoading, setInternalLoading] = useState(true);

  useEffect(() => {
    const periods: Record<string, boolean> = {};
    if (data && data.periods) {
      for (const period of data.periods) {
        // For YoY, we want both periods visible by default
        periods[period] = true;
      }
    }
    setKeyQuestionsPeriodToggles(periods);
  }, [data]);

  // Effect to check if data is ready
  useEffect(() => {
    if (data && data.otherChartData) {
      // Set a small delay to prevent flashing
      const timer = setTimeout(() => {
        setInternalLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [data]);

  const handleKeyQuestionsPeriodToggle = (t: string) => {
    setKeyQuestionsPeriodToggles((keyQuestionsPeriodToggles) => {
      const toggles = { ...keyQuestionsPeriodToggles };
      toggles[t] = !toggles[t];
      return toggles;
    });
  };

  // Determine if we should show loading state
  const showLoading = isLoading || internalLoading;

  const uuid = uuidv4();

  const [getPng, { ref }] = useCurrentPng();
  const [savedPng, setSavedPng] = useState<boolean>(false);

  async function saveChart() {
    let runningInterval = setInterval(async () => {
      if (savedPng) {
        clearInterval(runningInterval);
      }
      try {
        const png = await getPng();

        if (png && setImages && idx) {
          setImages((oldState: ImageMap) => {
            return {...oldState, [idx]: png}
          });
          setSavedPng(true);
          clearInterval(runningInterval);
        }
      }
      catch(error) {
        // Handle error silently
      }
    }, 1000)
  }

  // Transform data for grouped bar chart
  const transformDataForGroupedBars = () => {
    if (!data || !data.otherChartData || !data.periods) return [];
    
    // For grouped bars, we need to restructure the data
    // Each question should have values for both periods as separate properties
    return data.otherChartData;
  };

  if (showLoading) {
    return (
        <Card
            href={href}
            icon={keyQuestionsIcon}
            title={customName ? customName : "KEY QUESTIONS YEAR ON YEAR"}
            chartOptions={
              <React.Fragment>
                <button
                    className="Option"
                    onClick={() => handleSetKeyQuestionsChartType("Bar")}
                >
                  Bar
                </button>
                <button
                    className="Option"
                    onClick={() => handleSetKeyQuestionsChartType("Line")}
                >
                  Line
                </button>
              </React.Fragment>
            }
        >
          <div className="CardBody">
            <div className="ToggleFlex">
              <div className="BarToggle">
                {keyQuestionsPeriodToggles &&
                    Object.entries(keyQuestionsPeriodToggles).map(
                        ([key, value], index: number) => {
                          return (
                              <ToggleButton
                                  key={index}
                                  active={value}
                                  toggle={() => handleKeyQuestionsPeriodToggle(key)}
                              >
                                <div
                                    className="ToggleDot"
                                    style={{
                                      backgroundColor: getChartColors(keyQuestionsChartType)[index],
                                    }}
                                ></div>
                                <div className="ToggleLabel">{key}</div>
                              </ToggleButton>
                          );
                        }
                    )}
              </div>
            </div>
            <div className="Chart" style={{height: "300px"}}>
              <LoadingIndicator/>
            </div>
          </div>
        </Card>
    );
  }
  
  return (
      <Card
          href={href}
          icon={keyQuestionsIcon}
          title={customName ? customName : "KEY QUESTIONS YEAR ON YEAR"}
          chartOptions={
            <React.Fragment>
              <button
                  className="Option"
                  onClick={() => handleSetKeyQuestionsChartType("Bar")}
              >
                Bar
              </button>
              <button
                  className="Option"
                  onClick={() => handleSetKeyQuestionsChartType("Line")}
              >
                Line
              </button>
            </React.Fragment>
          }
      >
        <div className="CardBody" >
        <div className="ToggleFlex">
          <div className="BarToggle">
            {keyQuestionsPeriodToggles &&
              Object.entries(keyQuestionsPeriodToggles).map(
                ([key, value], index: number) => {
                  return (
                    <ToggleButton
                      key={index}
                      active={value}
                      toggle={() => handleKeyQuestionsPeriodToggle(key)}
                    >
                      <div
                        className="ToggleDot"
                        style={{
                          backgroundColor: getChartColors(keyQuestionsChartType)[index],
                        }}
                      ></div>
                      <div className="ToggleLabel">{key}</div>
                    </ToggleButton>
                  );
                }
              )}
          </div>
        </div>
        <div className="Chart"  style={{height: "300px"}}>
          <div className="ChartWrapper">
            {data && checkNullChartData(data.pieChartData.data) ? (
              <div style={{ minWidth: `${minWidth}px` }}>
                <ResponsiveContainer width="99%" height={300} key={uuid}>
                  {keyQuestionsChartType === "Bar" ? (
                    <BarChart
                      data={transformDataForGroupedBars()}
                      maxBarSize={30}
                      barGap={4}
                      barCategoryGap={10}
                      margin={{ top: 20, bottom: 30, right: 10, left: 10 }}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip
                        formatter={
                          data.options.yAxisType === "Percentage"
                            ? percentageFormatter
                            : undefined
                        }
                      />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={percentageFormatter}
                        domain={[(dataMin: number) => dataMin < 0 ? dataMin - 15 : 0, 100]}
                        ticks={ticks20}
                      />
                      {keyQuestionsPeriodToggles &&
                        Object.entries(keyQuestionsPeriodToggles).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Bar
                                  key={index}
                                  dataKey={key}
                                  fill={getChartColors(keyQuestionsChartType)[index]}
                                  radius={[3, 3, 0, 0]}
                                  isAnimationActive={false}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={percentageFormatter}
                                  />
                                </Bar>
                              );
                            } else {
                              return <React.Fragment key={index} />;
                            }
                          }
                        )}
                    </BarChart>
                  ) : keyQuestionsChartType === "Line" ? (
                    <LineChart
                      data={transformDataForGroupedBars()}
                      margin={{ top: 20, bottom: 20, right: 10, left: 10 }}
                      ref={ref}
                    >
                      <CartesianGrid vertical={false} strokeDasharray="3" />
                      <Tooltip
                        formatter={
                          data.options.yAxisType === "Percentage"
                            ? percentageFormatter
                            : undefined
                        }
                      />
                      <XAxis
                        dataKey="name"
                        tick={<Tick />}
                        axisLine={false}
                        tickLine={false}
                        padding={{ left: 30, right: 30 }}
                        interval={0}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={percentageFormatter}
                        domain={[0, 100]}
                        ticks={ticks20}
                      />
                      {keyQuestionsPeriodToggles &&
                        Object.entries(keyQuestionsPeriodToggles).map(
                          ([key, value], index: number) => {
                            if (value) {
                              return (
                                <Line
                                  key={index}
                                  dataKey={key}
                                  fill={getChartColors(keyQuestionsChartType)[index]}
                                  isAnimationActive={false}
                                  type="monotone"
                                  stroke={getChartColors(keyQuestionsChartType)[index]}
                                  dot={{
                                    stroke: getChartColors(keyQuestionsChartType)[index],
                                    strokeWidth: 2,
                                  }}
                                >
                                  <LabelList
                                    dataKey={key}
                                    position="top"
                                    formatter={percentageFormatter}
                                  />
                                </Line>
                              );
                            } else {
                              return <React.Fragment key={index} />;
                            }
                          }
                        )}
                    </LineChart>
                  ) : (
                    <React.Fragment />
                  )}
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="NoChartData">
                <p>No results found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default KeyQuestionsYoY;